<template>
<div class="updates-page">
  <section class="updates-hero">
    <div class="container">
      <div class="row justify-content-center text-center">
        <div class="col-lg-8">
          <p class="eyebrow">Changelog</p>
          <h1 class="hero-title">Product updates</h1>
          <p class="hero-sub">
            What we've shipped, month by month. Fresh items at the top.
          </p>
        </div>
      </div>
    </div>
  </section>

  <div class="updates-body">
    <div class="container">

      <div class="row updates-layout">
        <aside class="col-lg-3 d-none d-lg-block updates-toc-col">
          <nav class="updates-toc" aria-label="Jump to month">
            <h4>Browse by month</h4>
            <ul>
              <li v-for="month in monthList" :key="month.id">
                <a
                  :href="'#' + month.id"
                  :class="{ active: activeMonthId === month.id }"
                  @click.prevent="goToMonth(month.id)"
                >{{ month.label }}</a>
              </li>
            </ul>
          </nav>
        </aside>
        <div class="col-lg-9 updates-content-col" ref="updatesContent">
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">October 2026</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Live overlay camera
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Frame every team photo with the overlay showing on the live camera, so shots line up the first time.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Review each photo with its overlay, and retake it, before it is saved.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Design overlays in a redesigned editor with a sample photo preview and layer tools, and fine-tune the overlay on any single photo.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Download a whole day's photos as one zip file.
              </p>

              <p class="mt-4" style="font-size: large">
                New feature: Two-way text messaging
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Text customers from automations and from the console, and receive their replies.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Every email and text conversation with a customer sits on one Messages page, so anyone on the team can pick up the thread and reply.
              </p>

              <p class="mt-4" style="font-size: large">
                Flexible booking options
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Sell one event at several lengths, each with its own pricing, so groups book the experience that fits their time and budget.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Sell add-ons and one-off charges while creating a booking in the console, so the whole sale lands in one transaction.
              </p>

              <p class="mt-4" style="font-size: large">
                Smarter resource assignment
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Resource assignments rebalance across the whole day after every booking, cancellation, reschedule, or resource change, so idle gaps close and more slots stay bookable.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Block chosen events only while fewer than a set number of resources cover the booked time, so thin shifts are protected and full shifts keep selling.
              </p>

              <p class="mt-4" style="font-size: large">
                Simpler payment collection
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Connect Stripe in a few clicks from Payment settings, with no API keys to copy.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Sell gift cards at the counter with tap to pay on a POS device.
              </p>

              <p class="mt-4" style="font-size: large">
                More personal email marketing
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Run birthday campaigns on autopilot: every customer gets an email before their birthday with a single use promo code of their own.
              </p>

              <p class="mt-4" style="font-size: large">
                Proactive event pacing
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Smart pacing shows how long teams usually take on each objective while the current team is still on track, so hosts can spot a slowdown and offer a hint before the team falls behind.
              </p>

              <p class="mt-4" style="font-size: large">
                Streamlined multi-location management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Open a new location by copying an existing one with its events, schedules, templates, and settings, while bookings, customers, and payments stay behind.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Combine locations by merging one into another, or copy settings into an existing location.
              </p>

              <p class="mt-4" style="font-size: large">
                Location-verified time tracking
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Confirm staff are at the venue when they start a time tracking timer, so logged hours reflect time actually spent on site. Choose a warning or a hard block for each category.
              </p>
            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">September 2026</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Checklist review and approval
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Every completed checklist has a run details page with per-step outcomes, notes, issues, and photos, so a manager can see exactly what was done on a reset or an opening routine without asking.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Steps can collect a dropdown choice, multiple selections, text, or a number, and staff can add a note, an issue, or a photo to any step during the run.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Require approval on a checklist and review completed runs before they count as approved.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Save a partially completed checklist and resume it later, or share one run between staff over several days, with each step recording who completed it.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Record repair needs on a step and turn them into project tickets, so a broken prop found during a reset lands in the maintenance queue straight away.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Write step instructions in a larger, resizable rich text editor, attach several reference images to a step, and take several photos during a run.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Checklist runs and Checklist steps report types in Reports: one row per run with rollups for host performance, or one row per step with every step detail.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Add notes and tags from the tag library to checklists, runs, and steps, correct a completed checklist's times or event session, and archive and restore checklists from an Archived tab.
              </p>

              <p class="mt-4" style="font-size: large">
                Richer booking views
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Weekly and monthly views on the bookings list, Calendar, Resource calendar, and Kanban, so staff can plan the week or scan the month in whichever view they prefer.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                The Calendar view keeps event headers in sight while scrolling the day, and a calendar setting hides blocked slots that overlap a booking so the Calendar and Resource calendar show only what matters.
              </p>

              <p class="mt-4" style="font-size: large">
                More insight from the event tracker
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Live pacing on the event tracker shows whether a team is ahead, on pace, or behind past teams on each objective, so hosts know when a nudge will help.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                The customer screen timer is recorded when an event is completed and shown on the tracker Review tab and in Event history.
              </p>

              <p class="mt-4" style="font-size: large">
                Broader dashboard analytics
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Break down bookings and sales by traffic source, medium, and campaign, with a traffic attribution filter, so you can see which marketing channels bring in revenue.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Bar and pie widgets showing bookings by group size.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Select every location, or every location in a country, in one click from the dashboard Location filter and the widget Groups field.
              </p>

              <p class="mt-4" style="font-size: large">
                Expanded language and currency support
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Danish language and Danish krone (DKK) across the booking site, emails, console, and point of sale.
              </p>

              <p class="mt-4" style="font-size: large">
                Faster everyday workflows
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                In the schedule editor, clear only the selected days and copy one day's slots to other days, instead of rebuilding each day by hand. Closed schedules can be set more than a year in advance.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Cancel a transaction from one panel that handles its bookings, waivers, items, and gift cards together, in place of a separate step for each.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Resolve cart errors and delete abandoned carts in bulk by date range, with additional filters on the Carts page.
              </p>

              <p class="mt-4" style="font-size: large">
                Improved project tracking
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Automations can trigger when a project ticket's status changes and use status as a condition, so the right person hears about it the moment a ticket moves.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                List subtasks on the project board and log time against them in Time Tracker.
              </p>

              <p class="mt-4" style="font-size: large">
                More complete customer records
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                View archived customer profiles and restore them when one was archived by mistake.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                The purchaser's zip code from card payments is saved to the customer record.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Guardian email and phone number columns on the waiver report for minors.
              </p>
            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">August 2026</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Point of sale
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Take in-person card payments with the OTC POS app, branded payment pages, and Stripe smart readers, so walk-ups, add-ons, and balances due are settled at the front desk and land on the transaction like any other payment.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Tips on the POS app can be based on the amount due, the whole order, or the subtotal before taxes and fees, so the prompt matches how your venue tips.
              </p>

              <p class="mt-4" style="font-size: large">
                New feature: Request to book
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Offer time slots that customers request instead of book outright. Staff approve or deny each request, and the booking confirms and charges only on approval, which makes tentative availability, large groups, and staffing-dependent events bookable online without overcommitting.
              </p>

              <p class="mt-4" style="font-size: large">
                New feature: Email campaigns
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Send targeted marketing emails from OTC. Build an audience from booking, purchase, tag, and location filters, send to it, and see the revenue each campaign brings back. Billing is usage based, so you pay for what you send.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Preview and download the audience list while building it, and edit, delete, and review the recipients of saved audiences straight from the campaign builder.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                The email template builder gains custom HTML blocks and Biku AI content generation, so a campaign or automation can go from blank to sendable in a few minutes.
              </p>

              <p class="mt-4" style="font-size: large">
                Analytics and dashboards
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Dashboard and widget templates, plus cross-group analytics, so a multi-location operator can build a dashboard once and read every group on it.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Global filter bar on dashboards: set a date range, location, event, staff member, and more once and every widget follows.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Compare any widget against previous periods or the same period in prior years, with a default compare mode and period count saved per widget.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Line graphs group points by day, week, or month to match the selected date range, and additional data fields and sources are available to widgets.
              </p>

              <p class="mt-4" style="font-size: large">
                Tags
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Colored booking tags with a shared library, available in list filters, reports, email audiences, and dashboards, so "corporate", "VIP", or "birthday" means the same thing everywhere.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Group-wide tags applied from the event tracker or booking page automatically tag every customer on the booking, which turns a one-tap note during the event into a segment you can market to later.
              </p>

              <p class="mt-4" style="font-size: large">
                Bookings and scheduling
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Set a custom message, as text or a badge, on booking slots from the scheduler, so a slot can carry "last few spots" or "staff pick" wherever it appears.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Booking views flag time slots that are past the minimum booking notice and closed for online booking, so staff can see at a glance which slots only they can still fill.
              </p>

              <p class="mt-4" style="font-size: large">
                Purchases
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                UTM medium, campaign, and referrer are shown alongside source on transactions, carts, gift cards, and booking details, so you can trace a sale back to the campaign that produced it.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Refund option that pre-fills the amount due back to the customer on overpaid transactions.
              </p>

              <p class="mt-4" style="font-size: large">
                Waivers
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Generic waiver for walk-ins: guests scan a QR code and sign without a booking, so the waiver step no longer depends on a reservation existing first.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Waiver check-in stays on the previous day after midnight until a configurable time, so late-night sessions check in against the right day.
              </p>

              <p class="mt-4" style="font-size: large">
                Customers and staff access
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customer list broken down by group, with visibility limited to each staff member's assigned groups.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customer address, city, state, zip, and country columns and filters on the transaction, purchases, and payments reports.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Language support expanded with a per-location default language and a customer language switcher on each customer-facing surface.
              </p>

              <p class="mt-4" style="font-size: large">
                Platform
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Public status page at status.offthecouch.io with live uptime and incident history for every surface.
              </p>
            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">July 2026</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Saved views
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Filter any list the way you need it, save it as a named view, and reopen it from a bookmark menu. Saved views work across the platform, so the filters your team rebuilds every morning are now one click.
              </p>

              <p class="mt-4" style="font-size: large">
                Webhooks and the API
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Webhook notifications for bookings, transactions, payments, and events, configurable from the API tab, so your own tools can react the moment something happens in OTC.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Booking change events now cover date and time updates, group size changes, and reschedules, not just creation and cancellation.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Booking webhooks carry customer name, email, phone, and event name, so downstream systems get the context without a second lookup, and booking.created now fires when a booking is added to an existing transaction.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Photo counts and photo listings are available through the API.
              </p>

              <p class="mt-4" style="font-size: large">
                Reporting and analytics
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Summary reports: group any report by a field or time period to get counts and totals at a glance, with sortable columns.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New staff report showing which events each staff member worked, hosted, or collected tips on.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Report templates can save a filtered view that applies automatically on every run, so a report opens focused instead of raw.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customer report gains a Tags column and a tag filter, making segmented customer lists quick to pull.
              </p>

              <p class="mt-4" style="font-size: large">
                Bookings and scheduling
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Create booking and resource slots in bulk straight from a schedule template.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Event images can be targeted to mobile or desktop visitors, and portrait images are now supported on the uploader and across every booking site view.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Custom header and description for the Add ons step on the customer booking site.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Custom fields set to alert now raise their alert on internally created bookings too, so phone bookings get the same attention as online ones.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Booking activity log now records who was assigned, what changed on a reschedule, note content, and payment method.
              </p>

              <p class="mt-4" style="font-size: large">
                Resource management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Per location settings for buffer coverage and cross event buffer blocking, so multi site venues can tune availability rules independently.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                The event list is sorted by event name and time when assigning events to a resource slot.
              </p>

              <p class="mt-4" style="font-size: large">
                Emails and messaging
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Email automations can send to user roles, with recipients resolved from the current roster at send time, so staff changes never quietly break a workflow.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Advanced Emails can notify your team the moment a payment request link is paid.
              </p>

              <p class="mt-4" style="font-size: large">
                Waivers
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customizable text for the "Are you accompanying children?" step on check-in waivers.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Birthday entry by dropdown or calendar instead of typing the date, which removes a common source of check-in errors.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Option to show who booked each time slot on the traditional waiver slot picker.
              </p>

            </div>
          </card>

          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">June 2026</h3>

              <p class="mt-4" style="font-size: large">
                Booking site conversion upgrades
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Live countdown banner warns customers when a slot is about to close, creating urgency right at the moment of decision.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customers can read the full event description before picking a date and time, via a configurable "learn more" trigger on both card and list layouts, plus a separate booking page highlight field.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Custom slot statuses can now show on the booking site as bookable or sold out, with an optional message and link.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Configurable customer details on the booking page: phone, zip code, and address per booking site template, with per-venue Required, Alert, and Interaction toggles on custom fields.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Clearer deposit choice at checkout with selectable layouts and a custom deposit description.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Option to hide time slots once their minimum notice window passes, so customers never see slots they can't book.
              </p>

              <p class="mt-4" style="font-size: large">
                Smarter booking operations
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Hold the remaining seats on a partially-booked public time slot so customers can't book them.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Bulk-assign a staff member to many bookings at once by event, date, and time range, and set custom slot statuses from bulk actions.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New setting to split shared public booking slots into per-group cards on the kanban board, or keep them combined.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Upgrade to private reservation can now charge per-ticket pricing plus a fixed fee, not only a flat buyout.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Auto-block events starting at the same time as a new booking, and choose whether auto-unblock restores call-to-book slots or only blocked slots, per resource.
              </p>

              <p class="mt-4" style="font-size: large">
                Analytics and dashboards
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Duplicate individual widgets and entire dashboards, and copy dashboards and surveys to other company groups.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Resource staffing efficiency: a utilization report and dashboard widgets comparing staffed vs booked hours.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Payment method is now available as a dashboard data field and widget filter, so you can break down revenue by POS, cash, card, check, or gift card.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Custom field statistics can now be displayed on dashboards, and "Yesterday" joins the date range options for widgets and reports.
              </p>

              <p class="mt-4" style="font-size: large">
                Gift cards, promos, and inventory
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Gift card policy agreement now displays on customer transactions, giving you proof of acceptance in case of chargebacks.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Gift card expiration dates can show on delivery emails and PDF downloads, controlled per gift card type, and discounted gift cards can be blocked from combining with promo codes.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Promo code quantity rules now support a maximum or a range, not just a minimum, and promo codes inherit their group's settings automatically.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Sortable inventory list with per-item controls for where items sell and their add-on experiences.
              </p>

              <p class="mt-4" style="font-size: large">
                Waivers and photos
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Standardized, readable waiver record download with signature, available from every waiver screen.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Zip / postal code field added to the waiver Location section, saved to the customer profile and available in reporting.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Quick confirmation that all participants are opted in before sharing a photo, and an optional confirmation before deleting a team photo.
              </p>

              <p class="mt-4" style="font-size: large">
                Additional improvements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Cart abandoned trigger in advanced email automation, with configurable send timing.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Report a wrong or unhelpful Biku Bot answer with one tap, filed automatically as a support ticket.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New Staffing endpoint added to the Booking API.
              </p>
            </div>
          </card>

          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">May 2026</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Setup wizard
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                A guided onboarding experience that walks new accounts through getting set up, with an AI assistant, contextual knowledge base articles, and progress tracking along the way.
              </p>

              <p class="mt-4" style="font-size: large">
                Reimagined booking drawer
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                The booking drawer now replaces the legacy booking popup as the central place to manage a booking, with slot metadata shown right in the drawer title for at-a-glance context.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Manage every aspect of the booking without leaving the drawer: send customer emails and review delivery history, add items to the transaction, assign staff to tips, collect payment with inline tip and promo entry, and jump to booking notes from the calendar tile or the drawer.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Applied promo codes, fees, and taxes now show their names in the summary and can be removed line by line.
              </p>

              <p class="mt-4" style="font-size: large">
                Calendar workflow improvements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Drag-and-drop slot moves are now fully configurable: per-device toggle, custom snap interval, and an optional confirmation prompt before a move is applied.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Calendar slots can now be dragged in 5-minute increments for finer-grained scheduling.
              </p>

              <p class="mt-4" style="font-size: large">
                Payments, deposits, and cart
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Deposit amount is now the default payment when creating a booking internally for an event with a configured deposit.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Option to require credit card storage on transactions without a deposit, for customers paying in full up front.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Booking site cart now supports multiple gift card types in a single order.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Tax and fee waivers now persist on transactions so they are not lost when revisiting the booking later.
              </p>

            </div>
          </card>

          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">April 2026</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Custom fields library
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                A centralized library for managing custom fields once and linking them to any booking site template. Update a field in one place and the change applies everywhere it's used.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Custom fields are now supported across internal booking flows on the console, not just the public booking site.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Custom fields can now be added to any waiver type.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Display rules for custom fields now scope independently by event and gift card type.
              </p>

              <p class="mt-4" style="font-size: large">
                Waiver experience upgrades
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Easy check-in for returning customers across both traditional and lookup waiver flows.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Configurable wording and opt-in/opt-out semantics for email and photo consent on waivers.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                "Hide past slots" can now trigger on booking end time or start time, whichever fits your workflow.
              </p>

              <p class="mt-4" style="font-size: large">
                Booking workflow improvements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Change booking states directly from calendar view without switching into the kanban board first.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Assign and edit booking resources from the kanban board view.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Hotlink kanban subtasks to associated project management tickets for one-click navigation between work and the booking it belongs to.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Booked slots now sit on the top layer of calendar view, no longer obscured by blocked slots.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Delete notes on bookings directly from the resource calendar and kanban board views.
              </p>

              <p class="mt-4" style="font-size: large">
                Resource management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Lock a resource after its first booking assignment to prevent accidental reassignment.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Set resource category usage based on the number of tickets purchased.
              </p>

              <p class="mt-4" style="font-size: large">
                Additional improvements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Setting to make phone numbers required or optional on the console.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Gift card recipient as a send-to option in advanced email automations.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Photos can now be assigned per transaction on public bookings.
              </p>
            </div>
          </card>

          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">March 2026</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Kanban board for bookings
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Booking lifecycle workflow states and a Kanban board view give you a visual way to manage bookings through each stage of your process.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Filter the Kanban board by booking status and assign staff directly from the board for faster operations.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Subtask support with attachments and drag-and-drop reordering keeps task management organized.
              </p>

              <p class="mt-4" style="font-size: large">
                Purchase and payment improvements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Refund to gift card option and the ability to undo an external refund give you more flexibility when handling returns.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Edit gift card values after purchase and choose which games appear for gift card value options.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Edit payment dates for POS and cash payments and view detailed product and customer information in cart errors.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Option to require storing a credit card as part of the purchase or booking process for deposits.
              </p>

              <p class="mt-4" style="font-size: large">
                Event management upgrades
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Adjust timer speed during sessions to slow down or speed up the hint display timer to match the pace of each group.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Configurable time increment buttons for fine-tuned timer control.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Pressing play to start the timer now auto-plays the intro video for a seamless start to each session.
              </p>

              <p class="mt-4" style="font-size: large">
                Booking site and scheduling
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Additional display modes for booking pages.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Additional configurations for custom fields, including image popups and higher character limits for richer content.
              </p>

              <p class="mt-4" style="font-size: large">
                Additional improvements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Attach multiple player photos to a single email for a better post-game experience.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Due dates on project management tickets with Kanban view support for better deadline tracking.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Cart data download with pagination controls and the ability to pull all items into payment reports.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Configure the default login page for your company and a trial-ended popup for expired accounts.
              </p>

            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">February 2026</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Unified event tracker
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Game Tracker and Hint System have been merged into a single integrated feature for streamlined event management.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Support for "Puzzle Lines" and nested events enables more complex puzzle and event structures.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Streamlined navigation and user experience in the advanced event tracker for faster workflow.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Timer auto-resets when a tracker is submitted, ready for the next session.
              </p>

              <p class="mt-4" style="font-size: large">
                Booking site enhancements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Support for multiple booking pages lets you create separate booking experiences for different needs.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                List board layout option provides an alternative way to view and manage bookings.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Real-time availability updates on the public booking site ensure customers always see accurate slot information.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Phone number validation enhancement improves data quality for customer contact information.
              </p>

              <p class="mt-4" style="font-size: large">
                Resource and schedule management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Pre-event and post-event buffer times for resource availability prevent scheduling conflicts between back-to-back events.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Custom status slot handling during schedule updates provides better control over non-standard time slots.
              </p>

              <p class="mt-4" style="font-size: large">
                Additional improvements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Time tracking now supports switching between tasks without stopping and restarting timers.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Additional measurement options on the Purchase Dashboard provide deeper analytics insights.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Phone number added to the waiver history page for quick customer reference.
              </p>


            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">January 2026</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Square integration
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Square users can now store customer credit cards for faster repeat bookings.
              </p>

              <p class="mt-4" style="font-size: large">
                New feature: Resource view
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                A new calendar view option dedicated to resource management, allowing you to see staff and resource assignments at a glance.
              </p>

              <p class="mt-4" style="font-size: large">
                Booking customization
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Create custom booking slot statuses to match your workflow and business needs.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customizable default status colors for a personalized calendar experience.
              </p>

              <p class="mt-4" style="font-size: large">
                Gift card and promo code enhancements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Full activity log for gift cards provides complete tracking of all gift card transactions.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Cross-group promo codes allow promotions to work across multiple locations.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Gift card management improvements include filtering by inactive types, editing gift card types, and an enhanced customer workflow modal.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                "Buy X get Y free" promo codes let you create promotions based on other items in the cart.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Promo code template settings allow applying templates when adding codes to groups and bulk updating all codes in a group.
              </p>

              <p class="mt-4" style="font-size: large">
                Additional improvements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Cross-location transaction viewing in customer profiles shows bookings across all your locations.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Faster check-in for customers with shared email addresses streamlines the waiver process.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Option to automatically delete waivers when cancelling a booking keeps records clean.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New buttons on the Puzzle Tracker page in Game Tracker enhance gameplay control.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Reorder company groups to organize your locations as needed.
              </p>

            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">December 2025</h3>

              <p class="mt-4" style="font-size: large">
                New feature: System notifications
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                System notifications, alerts, and announcements keep you informed of important platform updates directly within OTC.
              </p>

              <p class="mt-4" style="font-size: large">
                New feature: Community API
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Community API with documentation for managing bookings enables third-party app integrations and custom solutions.
              </p>

              <p class="mt-4" style="font-size: large">
                Enhanced payment and Stripe integration
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Stripe integration enhancements include automatic customer creation during purchase, custom description fields with dynamic variables, and the ability to store customer credit cards for faster repeat bookings.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Decimal precision for fees percentage option provides more accurate pricing control.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to override price on inventory items for flexible pricing adjustments.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Setting on custom payments to default as unpaid vs paid gives more control over payment workflows.
              </p>

              <p class="mt-4" style="font-size: large">
                Booking experience improvements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Persistent filters on booking calendar and list views remain in place when navigating away from the page.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Calendar view filter by slot status for quicker access to relevant bookings.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Option to have booking page default to the earliest available date for customer convenience.
              </p>

              <p class="mt-4" style="font-size: large">
                Resource management enhancements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Modified resource availability controls now support unavailability settings for more precise scheduling.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to automatically unblock slots based on resource allotment gives greater flexibility in capacity management.
              </p>

              <p class="mt-4" style="font-size: large">
                Analytics and dashboards upgrades
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New "Purchase" and "Tips" data sources added to dashboards for comprehensive financial insights.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Company user filter on Games and Checklist dashboard data sources for staff-specific reporting.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New data field in Transactions report shows all purchases within a transaction.
              </p>

              <p class="mt-4" style="font-size: large">
                Photo and game management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Add overlays to images directly from game tracker in both advanced and simple modes.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Default auto-apply option for overlays streamlines photo processing.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Game Tracker personal filters and game filter for simple mode provide customized views.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Access or generate booking-specific waiver links for streamlined waiver collection.
              </p>

              <p class="mt-4" style="font-size: large">
                Email and messaging enhancements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Copy custom email templates and automation rules to other groups for consistent communication.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Add multi-column sections to emails allowing components to be placed side by side.
              </p>

            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">November 2025</h3>

              <p class="mt-4" style="font-size: large">
                Enhanced booking experience
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Flexible calendar default date options let you display the current date, a set number of days ahead, or a specific date.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customizable calendar view settings for slot sizes and layout preferences.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Event-driven internal calendar replaces polling for improved performance.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New option to upgrade public bookings to private bookings.
              </p>

              <p class="mt-4" style="font-size: large">
                Promo code & purchase improvements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Promo code grouping allows organizing promotions into manageable categories.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to set promo codes based on quantity for volume-based discounts.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Select promo code groups for bulk action management.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Refund options added for custom payment methods.
              </p>

              <p class="mt-4" style="font-size: large">
                Email template enhancements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Click and drag functionality for email template components enables easier design customization.
              </p>

              <p class="mt-4" style="font-size: large">
                Reporting & analytics upgrades
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Tip date column added to the transaction report for detailed gratuity tracking.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Download widget data as CSV for custom analysis.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Dashboard data source filtering by game for more targeted insights.
              </p>

              <p class="mt-4" style="font-size: large">
                Marketing
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Container file template for GTM integration simplifies Google Tag Manager setup.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Navigation persistence for UTM tracker values ensures accurate attribution tracking.
              </p>

            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">October 2025</h3>

              <p class="mt-4" style="font-size: large">
                Enhanced booking management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Flexible post-start time purchases allow customers to join activities even after they've begun.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                "All Day" booking slots with customizable labels simplify full-day event management.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Category-specific purchase limitations enable either-or booking scenarios.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Reactivation capability for cancelled bookings and transactions improves operational flexibility.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Gift card balance display shows remaining credit after deposits for transparent customer billing.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Promo code grouping organizes promotional campaigns for easier management and tracking.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Add-on visibility in both list and calendar views streamlines internal booking management.
              </p>

              <p class="mt-4" style="font-size: large">
                Advanced email & messaging
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Additional email template components provide greater design flexibility and customization options.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Streamlined email settings page for easier DNS configuration.
              </p>

              <p class="mt-4" style="font-size: large">
                Operations & workflow features
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customer archiving functionality maintains clean customer list.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Waiver copying between game slots accelerates setup for recurring events.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Project management "watcher" role enables stakeholder visibility without active participation.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Report download cancellation option improves system responsiveness for large data exports.
              </p>
            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">September 2025</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Custom email templates and automation
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Fully customizable email templates with test send capability for perfect messaging before deployment.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Custom template widgets allow tailoring automated communications to match your brand voice.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Flexible automation capability, allowing you to trigger emails for a wide variety of platform events.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Dedicated email domain infrastructure ensures reliable delivery and improved sender reputation.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Company name and location now prominently featured in emails for better brand recognition.
              </p>

              <p class="mt-4" style="font-size: large">
                Improved purchase management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customizable promo code error messages.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Internal cart error display provides clear feedback for any issues encountered by customers during checkout process.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Gift card delivery emails can now be resent directly from transaction details for better customer service.
              </p>

              <p class="mt-4" style="font-size: large">
                Advanced resource management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Resource categories enable logical grouping with game-specific category requirements for streamlined operations.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Game-specific resource linking prevents unwanted slot overlaps for precise scheduling control.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Activity logging for resource slots provides complete audit trail and operational transparency.
              </p>

              <p class="mt-4" style="font-size: large">
                System & booking enhancements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Intelligent phone number validation and automatic country-based formatting improves data quality.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Enhanced Authorize.net payment form.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Smart schedule updates prevent overlapping with existing blocked or call-to-book slots.
              </p>
            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">August 2025</h3>
              <p class="mt-4" style="font-size: large">
                Enhanced Payment Experience
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Flexible payment method layouts (buttons and dropdown) on booking sites for improved customer checkout experience.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Comprehensive payment tracking section displays all payment statuses (failed, pending, completed) in one centralized view.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Enhanced invoice customization with payment details and custom fields for better customer documentation.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Improved visibility of failed payments and transactions on customer profiles for faster issue resolution.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Added Swedish Krona (SEK) currency support for international customers.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Enhanced webhook security for Stripe payments for safer payment processing.
              </p>

              <p class="mt-4" style="font-size: large">
                Advanced Resource Management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New "Purchase" blocking unit type allows blocking time slots based on purchase events for granular scheduling control.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Apply resource management rules to specific games rather than entire game inventory for more flexible operations.
              </p>

              <p class="mt-4" style="font-size: large">
                New Feature: Time Tracker
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Built-in time tracking functionality for project management tickets or stand-alone enables better productivity monitoring.
              </p>
            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">July 2025</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Booking Source Tracking
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Track the source of bookings for enhanced marketing insights and campaign effectiveness analysis.
              </p>

              <p class="mt-4" style="font-size: large">
                Enhanced Waiver Management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Introducing lookup waivers with full support for public booking slots, streamlining the waiver process for customers.
              </p>

              <p class="mt-4" style="font-size: large">
                Advanced User Access Controls
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Restrict access to reports and dashboards by user, ensuring sensitive business data is only visible to authorized personnel.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New "view only" permissions for default user roles, providing greater control over user access levels.
              </p>

              <p class="mt-4" style="font-size: large">
                Enhanced Booking Experience
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Google Calendar events now automatically update when transaction details change, including payments and customer information.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Improved Google Analytics integration settings to send either full purchase amount or due amount based on your preference.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Improved partial payment UI on calendar booking flow for a more intuitive payment experience.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New setting to display available spots for public bookings on your booking site.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Added Greek language support for customer-facing booking sites.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Updated embedding code to better handle referral source tracking from parent domains.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customizable "Pay on Arrival" messaging to match your brand voice.
              </p>

              <p class="mt-4" style="font-size: large">
                Improved Reporting & Analytics
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Booking activity logs now appear on transaction details pages for complete audit trails.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                "Today" option added to dashboard widgets and reports for quick daily insights.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Booking source field added to purchase and transaction reports for comprehensive tracking.
              </p>

              <p class="mt-4" style="font-size: large">
                Advanced Resource Management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New Resource Priority System with depth chart assignment for more strategic event scheduling.
              </p>

              <p class="mt-4" style="font-size: large">
                Time tracking for Project Management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Time tracking functionality added to project management tickets for better productivity monitoring.
              </p>
            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">June 2025</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Project Management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Users can now create and assign internal tickets to track business projects, tasks, and operational initiatives, all within the platform.
                A streamlined way to collaborate and stay organized behind the scenes.
              </p>


              <p class="mt-4" style="font-size: large">
                New feature: Calendar Settings
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Users can now personalize their booking calendar view.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Settings such as resource column width, horizontal scroll, and the contents of timeslots are now adjustable.
              </p>

              <p class="mt-4" style="font-size: large">
                Enhancements to the purchasing experience
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Support for Euro to support our European customers.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to apply arbitrary discounts directly to transactions without the need for promo codes.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Control promo code visibility on the Payment Request page:
                Added a setting that allows users to hide the promo code field on a payment request, keeping the checkout experience clean and focused when discounts aren’t in use.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Transaction details page to include more granular information about gift card payments, including gift card type and remaining balance.
              </p>

              <p class="mt-4" style="font-size: large">
                Enhancements to reporting and dashboards
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New report type: Custom Fields - Support for reporting on custom fields collected during the booking process.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Payment reports now include Gift Card Type column and purchase reports capture promo code usage, offering deeper insight into transactions.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Additional dynamic date range options on reports for more flexible data analysis.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Option to aggregate payment methods by monetary amount on dashboards.
              </p>


              <p class="mt-4" style="font-size: large">
                Resource management improvements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to automatically block off events if the number of players across booked events exceed a certain number.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to link events to resources, enabling users to group and manage resources more efficiently.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Users can choose whether a resource’s start and end times count toward blocking rules, allowing for more precise scheduling.
              </p>

              <p class="mt-4" style="font-size: large">
                Game management changes
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Added a “send photo” button in the simple game tracker so that photos can be automatically sent to group upon event completion.
              </p>

            </div>
          </card>

          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">May 2025</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Public bookings
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Users can now configure events for public reservations. This new option allows individual guests to book tickets for shared booking time slots. Great for walk-ins, smaller parties, and maximizing capacity.
              </p>


              <p class="mt-4" style="font-size: large">
                Enhancements to bookings
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customize the look and feel of the booking experience with an expanded selection of font choices.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Added a new section for archived or cancelled tickets, giving users the ability to separate active tickets from cancelled ones.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Avoid scheduling conflicts with a new option that automatically blocks overlapping resource slots for the same event type.
              </p>

              <p class="mt-4" style="font-size: large">
                Changes to Customer Credit payment option
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customer credits can now be used across different groups or locations, making it easier for guests to redeem their balance for bookings and purchases.
              </p>

              <p class="mt-4" style="font-size: large">
                Enhancements to the event log
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New Filter: The event log now includes a date filter, making it easier to review activity within a specific time range.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                In the event log, clicking a cancelled booking’s slot ID now takes you directly to its transaction page, making it easier to track payment details and history.
              </p>

            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">April 2025</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Ticket overview for company bookings
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Easily view all tickets purchased under a company’s group booking. This new section provides a clear list of all attendees, making group management and check-in smoother than ever.
              </p>

              <p class="mt-4" style="font-size: large">
                Improvements to bookings and resource management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Set Maximum Participants per Pricing Category on event pricing: Limit the number of types of participants allowed in each pricing category, giving greater control over booking caps and availability.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to set an event’s resource requirement to zero, allowing it to be excluded from resource management calculations. Perfect for events or games that don't require any resources, giving users even more control over bookings and operations.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Control the order in which images appear in game settings, giving more flexibility in how content is displayed.
              </p>


              <p class="mt-4" style="font-size: large">
                Upgrades to gift cards
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Users can now apply promotions to specific gift cards only, allowing more control over discounts and special offers tied to individual gift card types.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Editing Gift Cards in bulk - Making changes to gift cards just got easier! Users can bulk-update and edit details across multiple gift cards in one go. Perfect for quick updates and large-scale adjustments.
              </p>

              <p class="mt-4" style="font-size: large">
                Enhancements to promo codes
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                More granular ways to apply promo codes - Users now have the ability to create promo codes that apply to specific items from their inventory, allowing more targeted discounts for selected products.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to apply bulk actions to promo codes, making it easier to manage and update multiple codes at once.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Strengthened security measures around applying promo codes during the booking process to prevent misuse.
              </p>


              <p class="mt-4" style="font-size: large">
                Improvements to payment settings
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Granular application of taxes and fees: Users have the ability to apply taxes and fees to individual games, inventory items, and gift card types, allowing more control over pricing and customization.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Improved reporting for inclusive tax type: Added clearer tax linkage and now accurately records the tax portion of transactions where prices include tax. Allows better transparency for users and simplifies accounting and reporting.
              </p>
            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">March 2025</h3>

              <p class="mt-4" style="font-size: large">
                Inventory management upgrades
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Add-on purchases: Users can now create add-ons that can be selected during the booking process.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Flexible inventory limits: Set unlimited quantities for inventory items and add-ons, or set purchase limits if needed.
              </p>

              <p class="mt-4" style="font-size: large">
                Improvements to purchases
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to permanently delete deactivated gift cards from transaction records, allowing users to keep reporting clean and organized.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Mark gift card types as internal-only, so they’re hidden from customers, but still usable by users' staff.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New fee setting allows users to choose whether fees apply after promotions and discounts.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Personalize the message guests see after making a booking, with details like their name, game, or date filled in automatically via dynamic variables.
              </p>

              <p class="mt-4" style="font-size: large">
                Dashboard improvements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Dashboard widgets now support accounting and custom date ranges, giving users more control over how data is displayed based on their reporting needs.
              </p>

              <p class="mt-4" style="font-size: large">
                Game management enhancements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Added a new optional timer in the Advanced Game Tracker, to aid Game Masters to track pace more easily during games.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Added a Custom Time Field to the Game Tracker, allowing users to set custom time for unique situations, and is also editable for past games.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Users have the ability to set internal nicknames for games, helping teams stay organized behind the scenes.
              </p>
            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">February 2025</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Modular dashboards
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Personalize dashboards by adding, removing, and rearranging widgets.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Users can display the data that matters most to them, whether it’s booking stats, performance graphs, transaction data, or customer insights. Users can transform widgets to suit their unique needs.
              </p>

              <p class="mt-4" style="font-size: large">
                Reporting upgrades
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Tip tracking – Enhanced Purchase reports includes columns for Tips and Tip Assignees, giving users better visibility into gratuity distribution.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Improved filtering capabilities across various fields for easier reporting.
              </p>

              <p class="mt-4" style="font-size: large">
                Booking system enhancements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customize text and background colors for booking slots on external calendars, creating the ability for personalized organization.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Adding activity logs when blocking slots: Users can track instances where slots are manually made unavailable, improving visibility on slot statuses.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Enhanced bulk booking activity logging, improving users’ ability to track mass changes to booking slots.
              </p>

              <p class="mt-4" style="font-size: large">
                Game management updates
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                View Total Game Time on the Game History Table: Users can easily see the duration of a past booking at-a-glance.
              </p>
            </div>
          </card>
          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">January 2025</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Product Roadmap
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Introducing the Product Roadmap, a powerful new tool that replaces the legacy Changelog. The roadmap provides users with real-time updates on everything happening within the platform, fostering better transparency and engagement.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Get a complete view of issues, bugs, enhancements, features, and initiatives. Allows users to stay informed about both community-submitted requests and our internally planned work.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Voting and follow system: users can cast votes to highlight tickets of interest, or follow tickets to receive notifications about progress and changes.
              </p>

              <p class="mt-4" style="font-size: large">
                Enhancements to the booking system
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Expanded bulk actions to provide more flexibility when managing bookings.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to apply custom pricing to multiple bookings in one go.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Easily attach notes to multiple bookings during bulk actions, helping users keep better records and communication.
              </p>

              <p class="mt-4" style="font-size: large">
                Improvements to the Square integration
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customizable Note field: Add notes to Square payments with support for dynamic variables, allowing users to give detailed context for each transaction.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to create a customer in Square during payment events, streamlining customer tracking and management.
              </p>
            </div>


          </card>

          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">December 2024</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Google Calendar integration
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Easily import and sync your Google Calendar with our system, making it simpler to manage your schedule.
              </p>

              <p class="mt-4" style="font-size: large">
                Enhancements to gift cards
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Gift card customization: users can adjust the text and appearance of gift cards.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Enhanced gift card controls: Ability to activate, deactivate, and edit fields on gift cards.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Option to waive fees on gift card purchases.
              </p>

              <p class="mt-4" style="font-size: large">
                Improvements to schedules and booking system
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Apply bulk actions to bookings, restricted only to specific time ranges, allowing users more precise control.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Clear all resource assignments in bulk, freeing up your resources to adjust staff booking assignments.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Performance enhancements to schedule creation.
              </p>

              <p class="mt-4" style="font-size: large">
                Waiver system enhancement
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New option to show or hide past booking slots on the waiver page, allowing more control over visibility.
              </p>
            </div>


          </card>

          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">November 2024</h3>

              <p class="mt-4" style="font-size: large">
                Expanded gift card functionality
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to define gift card types with additional custom settings,
                including activation, expiration, and delivery dates.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Option to sell gift cards at a discount by setting purchase value and purchase price.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to activate and deactivate individual gift card type on customer-facing site.
              </p>

              <p class="mt-4" style="font-size: large">
                Advanced resource management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                An updated auto-blocking algorithm. Tracking added when a resource is assigned to and freed from events.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Configure staff requirements and blocking options on events.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Granular configurations on resources, including custom labeling and user assignment.
                Ability to set the number of events a single resource can support at a time.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Bookings can be assigned to resource slots manually or automatically.
              </p>

              <p class="mt-4" style="font-size: large">
                UI polish
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to reorder Checklists, as well as open checklists in multiple tabs.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to duplicate Checklists and Games.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Payment request link now available for easy copying on the transaction details page.
              </p>
            </div>
          </card>

          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">October 2024</h3>

              <p class="mt-4" style="font-size: large">
                Enhancement to payments and transactions
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to create transactions independently of bookings for greater flexibility.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customize visibility of taxes and fees on product page.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                A dedicated promo codes management interface with additional features to seamlessly copy promo codes across business locations,
                view archived promo codes, and more.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Configure any number of custom payment methods for internal transactions.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                A new email template for gift card purchases.
              </p>

              <p class="mt-4" style="font-size: large">
                Improved user account management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Track user last login times.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Associate an existing account with a different email address.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New, more granular permissions.
              </p>

              <p class="mt-4" style="font-size: large">
                Booking system improvements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Support for multi-day slots.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Smart default pricing based on game settings.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Better scheduling tools, such as save and duplicate schedule settings.
              </p>
            </div>
          </card>

          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">September 2024</h3>

              <p class="mt-4" style="font-size: large">
                New feature: Inventory Management
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Introducing simple inventory management with ability to easily categorize and track inventory items.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Seamlessly apply inventory to existing or new transactions.
              </p>

              <p class="mt-4" style="font-size: large">
                Enhanced customer notifications
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Send payment requests with custom deposit amounts and custom payment options.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Automatically trigger refund confirmation emails when a refund is issued.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Cart abandonment email to recover potential lost sales.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Additional customization options on gift card email templates.
              </p>


              <p class="mt-4" style="font-size: large">
                Improvements to reports and event logging
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Detailed activity logs for each booking slot for deeper insights into your bookings.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Expanded reporting capabilities include a new Promo Codes report type to track promotion effectiveness.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Flexible date ranges across all reports, with the retained ability to easily download all time data.
              </p>

              <p class="mt-4" style="font-size: large">
                Improved photos overlays
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Support for additional dynamic variables on photo overlays to create a unique customer experience.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to layer and organize photo overlay layers.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Easy team name management for personalized photos
              </p>
            </div>
          </card>

          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">August 2024</h3>

              <p class="mt-4" style="font-size: large">
                Expanded payment and purchase options
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Cart Management: New cart abandonment section, listing abandoned and active carts, to help recover potential lost sales.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Currency Support: Added support for AUD and NZD currencies, catering to a wider international customer base.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Addition of preset gift card values and game-specific values on internal gift card purchases, offering more flexibility in gifting options.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Additional promo code field for internal gift card purchases, enabling targeted promotions.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to add multiple gift cards to existing transactions, increasing purchase convenience.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Internal payment collection now records the company user who processed the payment, improving accountability.
              </p>



              <p class="mt-4" style="font-size: large">
                Enhanced customer experience
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Multilingual Support: Customer-facing pages now available in French and Spanish, including Waivers, Bookings, Payments requests, Surveys, and Emails, enhancing accessibility for a broader audience.              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Improved Booking Interface: Option to show/hide company logo and pricing rate labels on the booking site.
              </p>



              <p class="mt-4" style="font-size: large">
                Advanced booking management controls
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to add additional bookings to existing transactions, enhancing customer convenience and operational efficiency.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New feature to assign company users to specific booking slots, improving staff management.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Bulk update options for booking slots, including minimum booking notice and maximum booking time adjustments.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Enhanced granularity in booking slot event logging for better tracking and analysis.
              </p>

              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Override price feature now modifies pricing category by type (flat or per person), offering more precise pricing control.
              </p>


              <p class="mt-4" style="font-size: large">
                Improved reporting and customer insights
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New "Payments" report type for enhanced financial analysis.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Games tab on Customer Details page now includes columns for notes, completion time, and hints, providing deeper insights into customer experiences and preferences.
              </p>


            </div>
          </card>

          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">July 2024</h3>
              <p class="mt-4" style="font-size: large">
                Dynamic pricing
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Introducing flexible, customizable pricing options for individual booking slots.
                Users now have the ability to configure pricing dynamically through the booking calendar interface or during event schedule creation.
                This enhancement enables sophisticated pricing strategies that can be adjusted based on factors such as demand fluctuations or staff availability.
              </p>


              <p class="mt-4" style="font-size: large">
                Enhanced gratuity features
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Integration of tipping functionality on the booking platform, offering both preset and custom gratuity options.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Flexible tip management on the transaction details page, allowing for addition or removal of gratuities, and assignment of tips to staff.
              </p>

              <p class="mt-4" style="font-size: large">
                Granular email event logging
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Implementation of a comprehensive email event log accessible via both the transaction details and customer profile pages.
                This feature enables efficient monitoring and management of all customer email interactions.
              </p>

              <p class="mt-4" style="font-size: large">
                Waiver system enhancements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customizable compact view:
                Introduction of a configurable compact layout option for the waiver page, optimizing screen real estate and improving user experience.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Kiosk mode:
                Development of a dedicated kiosk mode for the waiver page, facilitating efficient on-site waiver completion and management.
              </p>

              <p class="mt-4" style="font-size: large">
                Customer engagement metrics enhancement
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Implementation of new data fields to capture and monitor critical customer interaction milestones.
                Initial engagement timestamp records the date and time of a customer's first interaction with the company.
                Most recent interaction timestamp tracks the date and time of the customer's latest engagement.
              </p>

            </div>
          </card>

          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">June 2024</h3>
              <p style="font-size: large">
                Account cost tracking and billing
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" /> We added cost tracking across all account usage, broken down by feature.
                This allows us to charge our users only for what they actually use.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" /> The billing page allowing users to save their billing information, view upcoming charges for the billing period, as well as
                invoices, and ability to update the subscription plan.
              </p>
              <p class="mt-4" style="font-size: large">
                Enhancements to booking site payment methods
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                We now support multiple gift card or card payments in a single booking transaction. Users can stack multiple gift card payments and cover the remainder with credit card.
                This feature helps minimize the need for customers to contact the company directly to apply their gift card balance.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                We added several customization to the booking site, including the ability to hide the promo code field and change the placeholder
                text for the field allowing users more control over their customer experience on the booking site.
              </p>
              <p class="mt-4" style="font-size: large">
                Improvements to the internal booking calendar
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                We added small improvements to the booking calendar on the internal platform. On the list view, we added the increment/decrement date buttons to mirror the calendar view.
                We also update the pricing rate labels for flat pricing.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                We added persistent booking page settings, so that the setting/view mode chosen by the user would persist across sessions and devices.
              </p>
            </div>
          </card>

          <card>
            <div class="card-body">
              <h3 class="card-title text-uppercase">May 2024</h3>
              <p style="font-size: large">
                Photo overlays
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                New feature: overlays on player photos. User now have the ability to create custom overlays with optional dynamic variable and assign them to photos.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Photos with overlays added will now show up on game history pages, customer details, player portal, and customer email attachments.
              </p>

              <p class="mt-4" style="font-size: large">
                Developer API
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Customer can use the developer API to pull their transaction data across all groups under an account. API is authenticated via API token that can be retrieved and rotated under Company Settings page.
              </p>

              <p class="mt-4" style="font-size: large">
                Enhancements to payments
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                We added custom expiration setting for payment requests, enabling users to define the time after a payment request is sent after which the transaction and associated booking will be cancelled.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to mark a card payment as refunded without processing the refund which is useful for when a refund has already been processed via payment gateway.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Ability to override price on gift cards (internal only).
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Users can now create and configure custom payment methods. The order and description for the methods are completely user defined. The methods will show up on the booking site.
              </p>

              <p class="mt-4" style="font-size: large">
                Role-based access control
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                You can now define custom user roles and assign them to account users.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Default user roles (admin, manager, game master) remain unchanged and come as default for all new accounts.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Custom user roles also allow users to configure email notification settings, allowing users to opt out of receiving certain system emails.
              </p>

              <p class="mt-4" style="font-size: large">
                Activity log
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                We added account activity log, showing all state-changing actions performed on an account.
              </p>

              <p class="mt-4" style="font-size: large">
                General platform improvements
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                We added persistent filters across the platform pages with lists, such as  waivers, transactions, and more. Filter will now remain in place even after user refreshes the page or moves away from the page.
              </p>
              <p>
                <svg-icon name="arrow-right" :size="14" class="bullet-icon" />
                Performed major backend upgrades to database and email service.
              </p>
            </div>
          </card>

        </div>
      </div>

    </div>
  </div>
</div>
</template>

<script>

function slugifyMonth(text) {
  return text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

export default {
  name: "NewsAndUpdates",
  data() {
    return {
      monthList: [],
      activeMonthId: null,
      monthObserver: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.buildToc();
      this.setupObserver();
    });
  },
  beforeDestroy() {
    if (this.monthObserver) this.monthObserver.disconnect();
  },
  methods: {
    buildToc() {
      const root = this.$refs.updatesContent;
      if (!root) return;
      const headings = root.querySelectorAll('.card-title');
      const months = [];
      headings.forEach(h => {
        const label = h.textContent.trim();
        if (!label) return;
        const id = slugifyMonth(label);
        h.id = id;
        h.setAttribute('tabindex', '-1');
        months.push({ id, label, el: h });
      });
      this.monthList = months;
      if (months.length > 0) this.activeMonthId = months[0].id;
    },
    setupObserver() {
      if (typeof IntersectionObserver === 'undefined') return;
      this.monthObserver = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter(e => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible.length === 0) return;
          this.activeMonthId = visible[0].target.id;
        },
        { rootMargin: '-100px 0px -65% 0px', threshold: 0 }
      );
      this.monthList.forEach(m => this.monthObserver.observe(m.el));
    },
    goToMonth(id) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/marketing-tokens';

.updates-page { padding-top: 110px; padding-bottom: 80px; }

.updates-hero {
  position: relative;
  padding: 3rem 0 4rem;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: -150px; left: 50%;
    transform: translateX(-50%);
    width: 700px; height: 500px;
    background: radial-gradient(circle, rgba(225, 78, 202, 0.12), transparent 60%);
    z-index: 0;
    pointer-events: none;
  }
  .container { position: relative; z-index: 1; }
}

.hero-sub {
  color: $muted;
  font-size: 1.1rem;
  line-height: 1.6;
}

.updates-body { margin-top: 0; }

/* TOC sidebar */

.updates-layout {
  align-items: flex-start;
}

.updates-toc-col {
  position: sticky;
  top: 100px;
  align-self: flex-start;
}

.updates-toc {
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 1.25rem 1rem;

  h4 {
    color: $muted-2;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    margin: 0 0 0.85rem;
    padding: 0 0.5rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 70vh;
    overflow-y: auto;
  }

  li { margin-bottom: 0.05rem; }

  a {
    display: block;
    padding: 0.4rem 0.6rem;
    color: $muted;
    text-decoration: none;
    font-size: 0.85rem;
    border-radius: 6px;
    border-left: 2px solid transparent;
    transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;

    &:hover {
      color: white;
      background: rgba(255, 255, 255, 0.04);
      text-decoration: none;
    }

    &.active {
      color: white;
      background: rgba(225, 78, 202, 0.1);
      border-left-color: $primary;
      font-weight: 600;
    }
  }
}

/* Card re-skin: targets the existing <card> markup */
.updates-body ::v-deep .card {
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
  margin-bottom: 1.75rem;
  overflow: hidden;
  transition: border-color 0.2s ease, transform 0.2s ease;
  &:hover { border-color: rgba(225, 78, 202, 0.25); }
}

.updates-body ::v-deep .card-body {
  padding: 2rem 2.25rem;
  @media (max-width: 768px) { padding: 1.5rem; }
}

.updates-body ::v-deep .card-title {
  color: $primary;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

/* Group headings (the <p style="font-size: large"> per feature) */
.updates-body ::v-deep p[style*="font-size: large"] {
  color: white !important;
  font-size: 1.1rem !important;
  font-weight: 600;
  margin-top: 1.85rem !important;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
}

/* Bullet rows (the <p> with the arrow icon) */
.updates-body ::v-deep .card-body p {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
  line-height: 1.55;
  margin-bottom: 0.4rem;
  padding-left: 0;
}

.updates-body ::v-deep .bullet-icon {
  color: $decorative;
  margin-right: 0.4rem;
  vertical-align: -2px;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .updates-page { padding-top: 90px; }
  .updates-hero { padding: 2rem 0 2.5rem; }
  .hero-sub { font-size: 0.95rem; }
}
</style>
